import { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

// دالة مساعدة: تحوّل نتيجة getCroppedArea إلى Blob فعلي
async function getCroppedBlob(imageSrc, pixelCrop) {
  const image = await new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', reject);
    img.src = imageSrc;
  });

  const canvas = document.createElement('canvas');
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext('2d');

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.92);
  });
}

const CropImageModal = ({ imageSrc, fileName, onConfirm, onSkip, onCancel }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [processing, setProcessing] = useState(false);

  const onCropComplete = useCallback((_croppedArea, croppedAreaPx) => {
    setCroppedAreaPixels(croppedAreaPx);
  }, []);

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;
    setProcessing(true);
    try {
      const blob = await getCroppedBlob(imageSrc, croppedAreaPixels);
      const croppedFile = new File(
        [blob],
        fileName.replace(/\.[^/.]+$/, '') + '_cropped.jpg',
        { type: 'image/jpeg' }
      );
      onConfirm(croppedFile);
    } catch (err) {
      console.error('Crop error:', err);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[2100] p-4">
      <div className="bg-card rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col max-h-[95vh]">

        {/* Header */}
        <div className="bg-primary/5 border-b border-border px-5 py-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
              <Icon name="Crop" size={20} color="var(--color-primary)" />
              اقتصاص صورة الغلاف
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              اضبط المنطقة التي ستظهر في بطاقتك للعملاء
            </p>
          </div>
          <button
            onClick={onCancel}
            className="p-1.5 hover:bg-muted rounded-lg transition-smooth flex-shrink-0"
            aria-label="إغلاق"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-50 border-b border-blue-100 px-5 py-3 flex items-start gap-3">
          <Icon name="Info" size={18} className="text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800 leading-relaxed">
            للغلاف: يُفضّل قص الصورة. للمعرض: يمكنك رفعها مباشرة بدون قص.
          </div>
        </div>

        {/* Crop Area */}
        <div className="relative bg-black" style={{ height: '340px' }}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={16 / 9}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            showGrid={true}
            style={{
              containerStyle: { borderRadius: 0 },
            }}
          />
          {/* Ratio badge */}
          <div className="absolute top-3 right-3 bg-black/70 text-white text-xs font-bold px-2.5 py-1 rounded-full backdrop-blur-sm pointer-events-none">
            16 : 9
          </div>
        </div>

        {/* Zoom Slider */}
        <div className="px-5 py-3 border-b border-border bg-muted/30">
          <div className="flex items-center gap-3">
            <Icon name="ZoomOut" size={16} className="text-muted-foreground flex-shrink-0" />
            <input
              type="range"
              min={1}
              max={3}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1 accent-primary h-1.5 rounded-full cursor-pointer"
            />
            <Icon name="ZoomIn" size={16} className="text-muted-foreground flex-shrink-0" />
            <span className="text-xs text-muted-foreground w-10 text-left">
              {Math.round(zoom * 100)}%
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-1.5 text-center">
            اسحب الصورة أو استخدم الشريط لضبط التكبير
          </p>
        </div>

        {/* Actions */}
        <div className="px-5 py-4 flex flex-col sm:flex-row items-center gap-3">
          <Button
            variant="default"
            iconName={processing ? 'Loader' : 'Check'}
            iconPosition="right"
            onClick={handleConfirm}
            disabled={processing}
            className="w-full sm:flex-1"
          >
            {processing ? 'جاري المعالجة...' : 'تأكيد الاقتصاص'}
          </Button>
          <Button
            variant="outline"
            onClick={onSkip}
            disabled={processing}
            className="w-full sm:flex-1 text-muted-foreground"
          >
            رفع بدون اقتصاص
          </Button>
        </div>

      </div>
    </div>
  );
};

export default CropImageModal;
